import * as constants from '../constants/CONSTANT';
import jwt_decode from 'jwt-decode';
import {
  ENTITLEMENTS_KEY,
  ENTITLEMENTS_UPDATED_EVENT,
} from '../utils/entitlements';

/** Shared across tabs: any tab with user activity updates this so idle timeout is global, not per-tab. */
export const LAST_USER_ACTIVITY_SYNC_KEY = 'spark_last_activity_ts';

function touchSyncedLastActivity() {
  try {
    localStorage.setItem(LAST_USER_ACTIVITY_SYNC_KEY, String(Date.now()));
  } catch (e) {
    // ignore quota / private mode
  }
}

const authApi = {
  async login(username, password, tcode) {
    let response = await fetch(constants.API_BASE_URL + '/api/auth/login', {
      method: 'POST',
      mode: 'cors',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        username: username,
        password: password,
        tcode: tcode,
      }),
    });

    const result = await response.json();
    if (result.success) {
      localStorage.setItem('token', result.authToken);
      touchSyncedLastActivity();
      try {
        const decoded = jwt_decode(result.authToken);
        if (decoded?.tenantcode) {
          localStorage.setItem('tenantcode', decoded.tenantcode);
        }
      } catch (error) {
        // ignore token decode errors
      }
      if (result.refreshToken) {
        localStorage.setItem('refreshToken', result.refreshToken);
      }
      if (result.authSettings) {
        localStorage.setItem(
          'authSettings',
          JSON.stringify(result.authSettings)
        );
      }
    }
    return result;
  },

  /** Persist tokens from a successful login / MFA verify / MFA enroll response. */
  persistSession(result) {
    if (!result?.success || !result.authToken) return false;
    localStorage.setItem('token', result.authToken);
    touchSyncedLastActivity();
    try {
      const decoded = jwt_decode(result.authToken);
      if (decoded?.tenantcode) {
        localStorage.setItem('tenantcode', decoded.tenantcode);
      }
      if (typeof sessionStorage !== 'undefined') {
        sessionStorage.setItem('userinfo', JSON.stringify(decoded));
        if (decoded.email) {
          sessionStorage.setItem('system_email', decoded.email);
        }
      }
    } catch (error) {
      // ignore
    }
    if (result.refreshToken) {
      localStorage.setItem('refreshToken', result.refreshToken);
    }
    if (result.authSettings) {
      localStorage.setItem('authSettings', JSON.stringify(result.authSettings));
    }
    return true;
  },

  async verifyMfa(mfaToken, code) {
    const response = await fetch(
      constants.API_BASE_URL + '/api/auth/mfa/verify',
      {
        method: 'POST',
        mode: 'cors',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mfaToken, code }),
      }
    );
    const result = await response.json();
    if (result.success) {
      this.persistSession(result);
    }
    return result;
  },

  async mfaSetup(mfaToken) {
    const token = localStorage.getItem('token');
    const headers = { 'Content-Type': 'application/json' };
    if (token) headers.Authorization = token;
    const response = await fetch(
      constants.API_BASE_URL + '/api/auth/mfa/setup',
      {
        method: 'POST',
        mode: 'cors',
        headers,
        body: JSON.stringify(mfaToken ? { mfaToken } : {}),
      }
    );
    return response.json();
  },

  async mfaEnable(code, mfaToken) {
    const token = localStorage.getItem('token');
    const headers = { 'Content-Type': 'application/json' };
    if (token) headers.Authorization = token;
    const response = await fetch(
      constants.API_BASE_URL + '/api/auth/mfa/enable',
      {
        method: 'POST',
        mode: 'cors',
        headers,
        body: JSON.stringify({ code, mfaToken }),
      }
    );
    const result = await response.json();
    if (result.success && result.authToken) {
      this.persistSession(result);
    }
    return result;
  },

  async mfaStatus() {
    const token = localStorage.getItem('token');
    const response = await fetch(
      constants.API_BASE_URL + '/api/auth/mfa/status',
      {
        method: 'GET',
        headers: { Authorization: token },
      }
    );
    return response.json();
  },

  async mfaDisable(password, code) {
    const token = localStorage.getItem('token');
    const response = await fetch(
      constants.API_BASE_URL + '/api/auth/mfa/disable',
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: token,
        },
        body: JSON.stringify({ password, code }),
      }
    );
    return response.json();
  },

  async mfaRegenerateBackupCodes(code) {
    const token = localStorage.getItem('token');
    const response = await fetch(
      constants.API_BASE_URL + '/api/auth/mfa/backup-codes/regenerate',
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: token,
        },
        body: JSON.stringify({ code }),
      }
    );
    return response.json();
  },

  async mfaAdminStatus(userId) {
    const token = localStorage.getItem('token');
    const response = await fetch(
      constants.API_BASE_URL +
        '/api/auth/mfa/admin-status/' +
        encodeURIComponent(userId),
      {
        method: 'GET',
        headers: { Authorization: token },
      }
    );
    return response.json();
  },

  async mfaAdminReset(userId) {
    const token = localStorage.getItem('token');
    const response = await fetch(
      constants.API_BASE_URL + '/api/auth/mfa/admin-reset',
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: token,
        },
        body: JSON.stringify({ userId }),
      }
    );
    return response.json();
  },

  async social_login(google_profile, token) {
    const { email, name, picture, sub } = google_profile;
    ////
    let response = await fetch(
      constants.API_BASE_URL + '/api/auth/social_login',
      {
        method: 'POST',
        mode: 'cors',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: email,
          name: name,
          picture: picture.data ? picture.data.url : picture,
          sub: sub,
          password: token,
        }),
      }
    );

    const result = await response.json();

    if (result.success) {
      localStorage.setItem('token', result.authToken);
      touchSyncedLastActivity();
      try {
        const decoded = jwt_decode(result.authToken);
        if (decoded?.tenantcode) {
          localStorage.setItem('tenantcode', decoded.tenantcode);
        }
      } catch (error) {
        // ignore token decode errors
      }
      if (result.refreshToken) {
        localStorage.setItem('refreshToken', result.refreshToken);
      }
      if (result.authSettings) {
        localStorage.setItem(
          'authSettings',
          JSON.stringify(result.authSettings)
        );
      }
    }
    return result;
  },

  async refreshToken() {
    const refreshToken = localStorage.getItem('refreshToken');
    const tenantcode = localStorage.getItem('tenantcode');
    if (!refreshToken || !tenantcode) {
      return false;
    }

    const response = await fetch(
      constants.API_BASE_URL + '/api/auth/refresh',
      {
        method: 'POST',
        mode: 'cors',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ refreshToken, tenantcode }),
      }
    );

    if (!response.ok) {
      try {
        const errBody = await response.json();
        if (errBody?.code === 'ACCOUNT_INACTIVE') {
          this.logout('ACCOUNT_INACTIVE');
          return false;
        }
      } catch (e) {
        // ignore parse errors
      }
      return false;
    }

    const result = await response.json();
    if (result.success && result.authToken) {
      localStorage.setItem('token', result.authToken);
      touchSyncedLastActivity();
      try {
        const decoded = jwt_decode(result.authToken);
        if (decoded?.tenantcode) {
          localStorage.setItem('tenantcode', decoded.tenantcode);
        }
      } catch (error) {
        // ignore token decode errors
      }
      if (result.refreshToken) {
        localStorage.setItem('refreshToken', result.refreshToken);
      }
      if (result.authSettings) {
        localStorage.setItem(
          'authSettings',
          JSON.stringify(result.authSettings)
        );
      }
      return true;
    }

    return false;
  },

  async fetchEntitlements() {
    const token = localStorage.getItem('token');
    if (!token) return { success: false };
    const response = await fetch(constants.API_BASE_URL + '/api/auth/entitlements', {
      method: 'GET',
      headers: { Authorization: token },
    });
    const result = await response.json();
    if (result.success && result.entitlements) {
      console.log('[authApi] fetchEntitlements response enabledPackages:', result.entitlements?.enabledPackages);
      const payload = JSON.stringify(result.entitlements);
      if (typeof sessionStorage !== 'undefined') {
        sessionStorage.setItem(ENTITLEMENTS_KEY, payload);
      }
      try {
        localStorage.setItem(ENTITLEMENTS_KEY, payload);
      } catch (e) {
        // ignore quota / private mode
      }
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new Event(ENTITLEMENTS_UPDATED_EVENT));
      }
      return result;
    }
    return { success: false };
  },

  async fetchMyImage() {
    ////
    const token = localStorage.getItem('token');
    let response = await fetch(constants.API_BASE_URL + '/api/auth/myimage', {
      method: 'GET',
      //mode: "cors",

      headers: {
        Authorization: token,
      },
    });
    ////
    if (response.status === 200) {
      const fileBody = await response.blob();
      return fileBody;
    } else {
      return null;
    }
  },

  async fetchUserImage(userid) {
    ////
    const token = localStorage.getItem('token');
    let response = await fetch(
      constants.API_BASE_URL + '/api/auth/userimage/' + userid,
      {
        method: 'GET',
        //mode: "cors",

        headers: {
          Authorization: token,
        },
      }
    );
    ////
    const fileBody = await response.blob();
    return fileBody;
  },

  logout(reason = 'LOGOUT') {
    // Prefer a more specific deactivation message if logout races (e.g. refresh + App.js)
    const existingReason = sessionStorage.getItem('logout_reason');
    if (
      existingReason === 'ACCOUNT_INACTIVE' &&
      reason !== 'ACCOUNT_INACTIVE'
    ) {
      if (!window.location.pathname.startsWith('/login')) {
        window.location.href = '/login';
      }
      return;
    }

    const isSessionTimeout =
      reason === 'SESSION_EXPIRED' || reason === 'IDLE_TIMEOUT';
    const isAccountInactive = reason === 'ACCOUNT_INACTIVE';

    if (isSessionTimeout || isAccountInactive) {
      const message =
        reason === 'ACCOUNT_INACTIVE'
          ? 'Your account has been deactivated. Please contact your administrator.'
          : reason === 'IDLE_TIMEOUT'
            ? 'You have been logged out due to inactivity. Please log in again.'
            : 'You have been logged out. Please log in again.';
      sessionStorage.setItem('logout_reason', reason);
      sessionStorage.setItem('logout_message', message);
    } else {
      sessionStorage.removeItem('logout_reason');
      sessionStorage.removeItem('logout_message');
    }

    // Build login URL: for session timeout only, append redirect as URL param (not session)
    const excludedPaths = [
      '/login',
      '/reset-password',
      '/google-auth-callback',
      '/402',
      '/403',
      '/404',
    ];
    const currentPath = window.location.pathname;
    const isExcludedPath = excludedPaths.some(
      path => currentPath === path || currentPath.startsWith(path)
    );
    const hasValidRedirect =
      isSessionTimeout && !isExcludedPath && currentPath !== '/';
    const redirectValue = hasValidRedirect
      ? currentPath + window.location.search
      : null;

    const refreshToken = localStorage.getItem('refreshToken');
    const tenantcode = localStorage.getItem('tenantcode');
    if (refreshToken && tenantcode && !isAccountInactive) {
      fetch(constants.API_BASE_URL + '/api/auth/logout', {
        method: 'POST',
        mode: 'cors',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ refreshToken, tenantcode }),
      });
    }

    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('tenantcode');
    localStorage.removeItem('authSettings');
    localStorage.removeItem(LAST_USER_ACTIVITY_SYNC_KEY);
    sessionStorage.removeItem('token');
    sessionStorage.removeItem('r-t');
    sessionStorage.removeItem('userinfo');
    sessionStorage.removeItem('user_permissions');
    sessionStorage.removeItem('user_permissions_timestamp');
    sessionStorage.removeItem(ENTITLEMENTS_KEY);
    try {
      localStorage.removeItem(ENTITLEMENTS_KEY);
    } catch (e) {
      // ignore
    }

    const loginUrl = redirectValue
      ? `/login?redirect=${encodeURIComponent(redirectValue)}`
      : '/login';
    window.location.href = loginUrl;
  },

  /**
   * Parse an unauthorized API response and log the user out with the right reason.
   * Uses response.clone() so callers can still read the body afterward.
   */
  async handleUnauthorized(response) {
    let reason = 'LOGOUT';
    try {
      const data = await response.clone().json();
      if (data?.code === 'ACCOUNT_INACTIVE') {
        reason = 'ACCOUNT_INACTIVE';
      }
    } catch (e) {
      // ignore parse errors
    }
    this.logout(reason);
  },

  async resendUserWelcomeEmail(userId) {
    const token = localStorage.getItem('token');
    let response = await fetch(
      constants.API_BASE_URL + '/api/auth/resend-welcome-email',
      {
        method: 'POST',
        mode: 'cors',
        headers: {
          'Content-Type': 'application/json',
          Authorization: token,
        },
        body: JSON.stringify({ userId }),
      }
    );
    return await response.json();
  },

  async forgotPassword(username, tcode) {
    let response = await fetch(
      constants.API_BASE_URL + '/api/reset-password/forgot',
      {
        method: 'POST',
        mode: 'cors',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ username, tcode }),
      }
    );
    return await response.json();
  },
};

export default authApi;
